import React, {useState, useEffect, useRef, useContext, useMemo, useCallback} from 'react';
import {Checkbox, Collapse, Divider, Flex, Form, Pagination, Select, Spin, Badge, Space} from "antd";
import Button, {BUTTON_ICON_POSITION, BUTTON_TYPE} from "@common/Button";
import { ChevronRightRounded, DeleteRounded, FileDownloadRounded, FitScreenRounded,
    PreviewRounded, PrintRounded, SaveRounded, SplitscreenTwoTone, Add, CheckCircleOutline,
    Reorder, CloseRounded, SummarizeRounded, HelpRounded} from "@mui/icons-material";
import TestQuestionCard from "./components/TestQuestionCard";
import InputAntdInline from "@common/InputAntdInline";
import TableFilterControlsForm from "@common/TableFilterControlsForm";
import TableFilterControls from "@common/TableFilterControls";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import {useScreenMode} from "@common/context/ScreenModeContext";
import {BrandContext} from "@context/BrandContext";
import {useForm} from "antd/es/form/Form";
import Tabs from "@common/Tabs";
import Tab from "@common/Tab";
import {checkArrayVal, objectHasEntry, reIndexArray, swapItemsInArray} from "@common/utilityFunctions";
import ListAntD from "@common/ListAntD";
import EmptyDataCard from "@common/EmptyDataCard";
import ExamFilters from "./components/ExamFilters";
import TestContentSelect from "./components/TestContentSelect";
import {useFormFilterBadge} from "@hooks/useFormFilterBadge";
import TestFilters from "./components/TestFilters";
import DiagnosticTestContentSelect, {DiagnosticTabType} from "./components/DiagnosticTestContentSelect";
import DiagnosticTestFilters from "./components/DiagnosticTestFilters";
import {SELECT_LEVELS} from "@common/SyllabusTopicConceptSelect";
import Tooltip from "@common/Tooltip";
import PreviewTestDialog from "../PreviewTestDialog";
import ReorderView from "./components/ReorderView";
import {
    searchTestQuestionsPaged,
    getAndSetTestData,
    getAndSetTestQuestionData,
    saveDescription,
    saveName,
    saveTestData, editTestQuestionInCMS
} from "./axiosRequests/axiosRequests";

import {useBreadcrumbComponent} from "@common/useBreadcrumbComponent";
import TestSkillSummaryDialog from "../TestSkillSummaryDialog";
import TestHelpDialog from "../TestHelpDialog";
import {UserContext} from "@context/UserContext";
import {useIndexRoving, usePaginationRoving} from "@hooks/useIndexRoving";
import ButtonsGroup from "@common/ButtonsGroup";
import {getAccessibleItemRender} from "@common/higherOrderComponents/paginationUtils";
import {useScreenReaderAnnouncement} from "@hooks/useScreenReaderAnnouncment";
import {ContentContext} from "@context/ContentContext";

export const ContentTabs = {
    Text: {val: '0', display: 'Text', hash: 'text', selectKey: 'text-tab-select'},
    Curriculum: {val: '1', display: 'Curriculum', hash: 'curriculum', selectKey: 'curriculum-tab-select'}
}
const DEFAULT_PAGE_SIZE = 5;

const View = Object.freeze({
    QUESTION_BANK: "Question Bank",
    SELECTED_QUESTIONS: "Selected Questions"
});

const tagsData = [View.QUESTION_BANK, View.SELECTED_QUESTIONS];

const TestEdit = ({testId, showBreadCrumbs = false}) => {

    const [title, setTitle] = useState(''),
        [description, setDescription] = useState(''),
        // [assessmentType, setAssessmentType] = useState(''),
        contentPanelsRef = useRef(null),
        pendingSelectedFocusIndexRef = useRef(null),
        pendingBankFocusIndexRef = useRef(null),
        questionBankHeadingRef = useRef(null),
        selectedQuestionsHeadingRef = useRef(null),
        [questionFiltersSearchForm] = useForm(),
        {mobileScreenMode} = useScreenMode(),
        brandConfig = useContext(BrandContext),
        userConfig = useContext(UserContext),
        [testData, setTestData] = useState({}),
        [useSections, setUseSections] = useState(false), //sections are used for Exam - PDFs Test type
        [testDataInit, setTestDataInit] = useState(false),
        [isPreviewDialogOpen, setPreviewDialogOpen] = useState(false),
        [isSkillSummaryDialogOpen, setSkillSummaryDialogOpen] = useState(false),
        [isHelpDialogOpen, setHelpDialogOpen] = useState(false),
        [showQuestionBank, setShowQuestionBank] = useState(true),
        [showEditOrderView, setShowEditOrderView] = useState(false),
        [isQuestionBankExpandAll, setQuestionBankExpandAll] = useState(false),
        [isSelectedQuestionsExpandAll, setSelectedQuestionsExpandAll] = useState(false),
        [showSelectedQuestions, setShowSelectedQuestions] = useState(true),
        [sectionTabs, setSectionTabs] = useState([]),
        [sectionsObj, setSectionsObj] = useState(null),
        [selectedQuestionsSection, setSelectedQuestionsSection] = useState([]),
        [contentTabVal, setContentTabVal] = useState('0'),
        [sectionTabVal, setSectionTabVal] = useState(null),
        [searchTestQuestionsParams, setSearchTestQuestionsParams] = useState({}),
        [testQuestionData, setTestQuestionData] = useState([]),
        [reorderData, setReorderData] = useState({}),
        [pagedTestQuestionData, setPagedTestQuestionData] = useState([]),
        [selectedQuestionIds, setSelectedQuestionIds] = useState([]),
        [checkedQuestionIds, setCheckedQuestionIds] = useState([]),
        [expandedQuestionBankIds, setExpandedQuestionBankIds] = useState([]),
        [currentQuestionBankPageIds, setCurrentQuestionBankPageIds] = useState([]),
        [questionBankQuestions, setQuestionBankQuestions] = useState([]),
        [currentQuestionBankPageData, setCurrentQuestionBankPageData] = useState([]),
        [expandedSelectedQuestionIds, setExpandedSelectedQuestionIds] = useState([]),
        [selectedTags, setSelectedTags] = useState(tagsData),
        reorderViewRef = useRef(null),
        reorderBtnRef = useRef(null),
        [tagsBeforeReordering, setTagsBeforeReordering] = useState(tagsData),
        [searchSkillData,setSearchSkillData] = useState({}),
        [questionLevelNames,setQuestionLevelNames] = useState([]),
        {
            onLoadComplete,
            handleValuesChange: handleBadgeValuesChange
        } = useFormFilterBadge(questionFiltersSearchForm),
        syllabusId = Form.useWatch('syllabusId', questionFiltersSearchForm),
        [currentPage, setCurrentPage] = useState(1),
        [changesSaved, setChangesSaved] = useState(true),
        [initTestLoading, setInitTestLoading] = useState(true),
        [saveTestLoading, setSaveTestLoading] = useState(false),
        [saveTestNameLoading, setSaveTestNameLoading] = useState(false),
        [saveTestDescriptionLoading, setSaveTestDescriptionLoading] = useState(false),
        BreadcrumbBar = useBreadcrumbComponent({ dropEndingSeparator: true });
    
    const reorderTopRef = useRef(null);

    const { setSrAnnouncement, LiveRegion, srAnnouncement } = useScreenReaderAnnouncement();

    useEffect(() => {
        if (initTestLoading) {
            setSrAnnouncement("Loading test editor...");
        } else {
            setSrAnnouncement("Test editor loaded.");
        }
    }, [initTestLoading]);

    const handleSaveTest = () => {
        // question length greater than max question amount is handled by the server.
        setSaveTestLoading(true);
        const callback = () => {
            const updateData = testQuestionData.length > 0 ? reIndexArray([...testQuestionData], ['questionSeq','originalSeq']) : [];

            setTestQuestionData(updateData);
            setSaveTestLoading(false);
            setChangesSaved(true);
        }
        saveTestData({
            callback: callback,
            body: {...changeDataForSave(testQuestionData)},
            params: {testId: testData.testId},
            brandConfigTestName:brandConfig.testName
        });
    }
    const changeDataForSave = (questionData) => {
        const questionIds = [],
            conceptIds = [];
        questionData.forEach((question) => {
            questionIds.push(question.questionId);
            conceptIds.push(question.concept?.conceptId);
        })
        return {questionIds: questionIds, conceptIds: conceptIds};
    }

    const handlePrintTest = () => {
        printTestWithColumn(1, testId);
    }

    const handleDownloadDoc = () => {
        downloadWordDoc(testId);
    }

    const handlePreviewTest = () => {
        setPreviewDialogOpen(true);
    }

    const handleSkillSummary = () => {
        setSkillSummaryDialogOpen(true);
    }

    useEffect(()=>{
        const isQuestionBankVisible = selectedTags.includes(View.QUESTION_BANK),
            isSelectedQuestionsVisible = selectedTags.includes(View.SELECTED_QUESTIONS);
        setShowQuestionBank(isQuestionBankVisible);
        setShowSelectedQuestions(isSelectedQuestionsVisible);
    },[selectedTags])

    useEffect(() => {
        if (pendingBankFocusIndexRef.current !== null) {
            const bankPanel = document.querySelector('#question-bank-list');

            if (bankPanel) {
                const remainingItems = Array.from(bankPanel.querySelectorAll('.ch-list-item'));

                if (remainingItems.length > 0) {
                    let targetIndex = pendingBankFocusIndexRef.current;

                    // Move to the next card so the user can rapidly press Enter
                    if (targetIndex + 1 < remainingItems.length) {
                        targetIndex++;
                    }

                    const targetCard = remainingItems[targetIndex];

                    const targetButton = targetCard.querySelector('.js-add-question-btn');

                    if (targetButton && !targetButton.disabled) {
                        setTimeout(() => targetButton.focus(), 0);
                    } else {
                        const anyFocusable = targetCard.querySelector('button:not([disabled]), input:not([disabled]):not([type="hidden"])');
                        if (anyFocusable) {
                            setTimeout(() => anyFocusable.focus(), 0);
                        } else {
                            questionBankHeadingRef.current?.focus();
                        }
                    }
                } else {
                    questionBankHeadingRef.current?.focus();
                }
            }

            pendingBankFocusIndexRef.current = null;
        }
    }, [selectedQuestionIds]);

    /* remove selectedQuestion focus management */
    useEffect(() => {
        // If we have a pending focus target from a deletion...
        if (pendingSelectedFocusIndexRef.current !== null) {
            const selectedPanel = document.querySelector('#selected-questions-list');

            if (selectedPanel) {
                const remainingItems = Array.from(selectedPanel.querySelectorAll('.ch-list-item'));

                if (remainingItems.length > 0) {
                    // For deletions, the NEXT item automatically slides up into the exact same index.
                    // So we do NOT add +1 here.
                    const targetIndex = pendingSelectedFocusIndexRef.current < remainingItems.length
                        ? pendingSelectedFocusIndexRef.current
                        : remainingItems.length - 1; // If we deleted the last item, focus the new last item

                    const targetCard = remainingItems[targetIndex];

                    // Find all interactive, non-disabled elements on the new card
                    const focusableButtons = Array.from(targetCard.querySelectorAll('button:not([disabled]), input:not([disabled]):not([type="hidden"])'));

                    if (focusableButtons.length > 0) {
                        // Try to specifically find the "Remove" button.
                        // (Ensure the aria-label on your Delete/Remove button contains the word "remove")
                        const targetButton = targetCard.querySelector('.js-remove-question-btn');

                        if (targetButton && !targetButton.disabled) {
                            setTimeout(() => targetButton.focus(), 0);
                        } else {
                            const anyFocusable = targetCard.querySelector('button:not([disabled]), input:not([disabled])');
                            if (anyFocusable) {
                                setTimeout(() => anyFocusable.focus(), 0);
                            } else {
                                selectedQuestionsHeadingRef.current?.focus();
                            }
                        }
                    } else {
                        // Fallback: if no focusable buttons are left on that card
                        selectedQuestionsHeadingRef.current?.focus();
                    }
                } else {
                    // Fallback: the list is completely empty now
                    selectedQuestionsHeadingRef.current?.focus();
                }
            }

            // Clear the ref so this doesn't run again until the next delete
            pendingSelectedFocusIndexRef.current = null;
        }
    }, [pagedTestQuestionData]);

    useEffect(()=>{
        if(showEditOrderView){
            if(useSections){
                const reorderSectionData = {sections:{}};
                Object.entries(sectionsObj).map(([key, value]) => {
                    const section = sectionsObj[key],
                        totalPages = Math.ceil(section.questions.length / DEFAULT_PAGE_SIZE),
                        columns = {},
                        columnOrder = [];
                    for(let i = 0; i < totalPages; i++){
                        const colKey = 'col-'+(i+1);
                        columnOrder.push(colKey);
                        const questionIds = [];
                        for(let j = 0; j < DEFAULT_PAGE_SIZE; j++){
                            if(((i*DEFAULT_PAGE_SIZE)+j) < section.questions.length){
                                questionIds.push('q-'+((i*DEFAULT_PAGE_SIZE)+(j+1)));
                            }
                        }
                        columns[colKey] = {title:'Page '+(i+1), questionIds:questionIds}
                    }
                    const questions = {};
                    section.questions.forEach((item, index)=>{
                        const questionKey = 'q-'+(index+1);
                        questions[questionKey] = {...item, id: questionKey};
                    });
                    reorderSectionData.sections[key] = {questions, columns, columnOrder }
                });
                setReorderData({...reorderSectionData});
            }else{
                const totalPages = Math.ceil(testQuestionData.length / DEFAULT_PAGE_SIZE);
                if(totalPages > 0){
                    const columns = {},
                        columnOrder = [];
                    for(let i = 0; i < totalPages; i++){
                        const colKey = 'col-'+(i+1);
                        columnOrder.push(colKey);
                        const questionIds = []
                        for(let j = 0; j < DEFAULT_PAGE_SIZE; j++){
                            if(((i*DEFAULT_PAGE_SIZE)+j) < testQuestionData.length){
                                questionIds.push('q-'+((i*DEFAULT_PAGE_SIZE)+(j+1)));
                            }
                        }
                        columns[colKey] = {title:'Page '+(i+1), questionIds:questionIds}
                    }
                    const questions = {};
                    testQuestionData.forEach((item, index)=>{
                        const questionKey = 'q-'+(index+1);
                        questions[questionKey] = {...item, id: questionKey};
                    });
                    setReorderData({questions, columns, columnOrder});
                }
            }
            if (showEditOrderView && reorderTopRef.current) {
                reorderTopRef.current.focus();
            }
        }
    },[showEditOrderView]);

    const handleReorder = ()=>{
        if (reorderViewRef.current) {
            const finalData = reorderViewRef.current.getFinalData();
            if(useSections){
                const updatedSections = {...sectionsObj};
                const flattened = [];
                let globalCounter = 0;
                sectionTabs.forEach(tab => {
                    const sectionKey = tab.key;

                    if (finalData[sectionKey]) {
                        const sectionReorderData = finalData[sectionKey];
                        const updatedQuestionsInNewOrder = [];

                        sectionReorderData.columnOrder.forEach((colOrderKey) => {
                            sectionReorderData.columns[colOrderKey].questionIds.forEach((questionKey) => {
                                const q = { ...sectionReorderData.questions[questionKey] };

                                q.sectionIndex = updatedQuestionsInNewOrder.length;
                                q.questionSeq = globalCounter++;

                                updatedQuestionsInNewOrder.push(q);
                                flattened.push(q);
                            })
                        });
                        updatedSections[sectionKey].questions = updatedQuestionsInNewOrder;
                    }
                });

                setSectionsObj(updatedSections);
                setTestQuestionData(flattened);

                if (sectionTabVal && updatedSections.hasOwnProperty(sectionTabVal)) {
                    setSelectedQuestionsSection([...updatedSections[sectionTabVal].questions]);
                }
            } else {
                const updatedQuestionsInNewOrder = [];
                finalData.columnOrder.forEach((colOrderKey) => {
                    finalData.columns[colOrderKey].questionIds.forEach((questionKey) => {
                        const q = { ...finalData.questions[questionKey] };
                        q.sectionIndex = updatedQuestionsInNewOrder.length;
                        q.questionSeq = updatedQuestionsInNewOrder.length;

                        updatedQuestionsInNewOrder.push(q);
                    });
                });
                setTestQuestionData(updatedQuestionsInNewOrder);
            }
            setCurrentPage(1);
        }
        changeMadeToTest();
        setSrAnnouncement("Questions reordered successfully.");
    }
    const handleSaveName = (val) => {
        if (val !== title) {
            setSaveTestNameLoading(true);
            const callback = () => {
                setSaveTestNameLoading(false);
            }
            saveName({testId: testId, name: val, brandConfigTestName:brandConfig.testName}, callback);
        }
    }
    const handleCheckboxChange = (checked,questionId)=>{
        let updateCheckboxState;
        if(checked){
            updateCheckboxState = checkedQuestionIds.filter((id)=>{return id !== questionId})
        }else{
            updateCheckboxState = [...checkedQuestionIds,questionId];
        }
        setCheckedQuestionIds(updateCheckboxState);
    }

    const handleSaveDescription = (val) => {
        if (val !== description) {
            setSaveTestDescriptionLoading(true);
            const callback = () => {
                setSaveTestDescriptionLoading(false);
            }
            saveDescription({testId: testId, description: val, brandConfigTestName:brandConfig.testName}, callback);
        }
    }

    const changeMadeToTest = ()=>{
        setChangesSaved(false);
    }

    const addQuestionsToTest = (questionArr) => {
        let updateObj = {...sectionsObj};
        let newTabVal = sectionTabVal;

        // Track newly added sections that don't exist in the tabs yet
        const newSectionKeys = [];

        questionArr.forEach((questionData, idx) => {
            const updateQuestion = {...questionData};
            if (useSections) {
                const sectionName = updateQuestion.section;
                if (!updateObj[sectionName]) {
                    updateObj[sectionName] = { name: sectionName, hash: sectionName, questions: [] };
                    if (!sectionTabs.find(t => t.key === sectionName) && !newSectionKeys.includes(sectionName)) {
                        newSectionKeys.push(sectionName);
                    }
                } else if (!updateObj[sectionName].questions) {
                    updateObj[sectionName].questions = [];
                }
                updateObj[sectionName].questions.push(updateQuestion);

                if (newTabVal === null && idx === questionArr.length - 1) {
                    newTabVal = sectionName;
                }
            } else {
                updateQuestion.index = testQuestionData.length;
            }
        });

        if (useSections) {
            // Combine existing tab keys in strict order with any brand new section keys
            const orderedKeys = [...sectionTabs.map(t => t.key), ...newSectionKeys];

            let globalCounter = 0;
            const flattened = [];

            // Explicitly map local (sectionIndex) and global (questionSeq) indices
            orderedKeys.forEach(key => {
                if (updateObj[key]) {
                    updateObj[key].questions = updateObj[key].questions.map((q, localIdx) => {
                        const updatedQ = {
                            ...q,
                            sectionIndex: localIdx,
                            questionSeq: globalCounter++
                        };
                        flattened.push(updatedQ);
                        return updatedQ;
                    });
                }
            });

            setSectionsObj(updateObj);
            setTestQuestionData(flattened);

            if (newTabVal && updateObj[newTabVal]) {
                setSelectedQuestionsSection([...updateObj[newTabVal].questions]);
                if (sectionTabVal === null) setSectionTabVal(newTabVal);
            }
        } else {
            const combined = [...testQuestionData, ...questionArr];
            const reindexed = combined.map((q, idx) => ({
                ...q,
                questionSeq: idx,
                sectionIndex: idx
            }));
            setTestQuestionData(reindexed);
        }

        changeMadeToTest();
        setSrAnnouncement(`${questionArr.length} question${questionArr.length !== 1 ? 's' : ''} added to the test.`);
    }

    const searchTestQuestions = () => {
        const newTestQuestionsParams = getTestQuestionsParams();

        if(testData.autoMark){
            newTestQuestionsParams.questionAnswerTypeGroup = 'MULTIPLE_CHOICE';
        }

        setSearchTestQuestionsParams(prevParams => {
            if (JSON.stringify(prevParams) === JSON.stringify(newTestQuestionsParams)) {
                return prevParams;
            } else {
                handleBadgeValuesChange();
                return newTestQuestionsParams;
            }
        });
    }

    const getTestQuestionsParams = () => {
        let testParams = {...questionFiltersSearchForm.getFieldsValue()};

        if (testParams.testDifficultyLevel === undefined || testParams.testDifficultyLevel === 'All') {
            //remove param so it searches all difficulties
            delete testParams.testDifficultyLevel;
        }
        if (testParams.conceptId === 0) {
            testParams.conceptId = null;
        }
        if (testParams.topicId === 0) {
            testParams.topicId = null;
        }
        if (testParams.syllabusId === 0) {
            testParams.syllabusId = null;
        }
        if (testParams.syllabusGroupId === 0) {
            testParams.syllabusGroupId = null;
        }
        if (testParams.examStyle === 'All') {
            delete testParams.examStyle;
        }
        if (testParams.section === 'All') {
            delete testParams.section;
        }
        if (testParams.examYear === 'All') {
            delete testParams.examYear;
        }
        if (testParams.examNumber === 'All') {
            delete testParams.examNumber;
        }
        if (testParams.testDifficultyLevel === 'All') {
            delete testParams.testDifficultyLevel;
        }
        if (testParams.useCalculator === 'All') {
            delete testParams.useCalculator;
        } else if (testParams.hasOwnProperty('useCalculator')) {
            testParams.useCalculator = testParams.useCalculator.toLowerCase() === 'true';
        }
        if (testParams.questionAnswerTypeGroup === 'All') {
            delete testParams.questionAnswerTypeGroup;
        }
        testParams.testPurpose = testData.testPurpose;

        if (testParams.hasOwnProperty('filterCategory') && Array.isArray(testParams.filterCategory)) {
            if (testParams.filterCategory.includes('exclusive-to-tests')) {
                testParams.digitalOnly = true;
            }
            delete testParams.filterCategory;
        }

        if (testData.skillCode === 'All') {
            delete testData.skillCode;
        }

        return testParams;
    }

    const removeQuestionsFromTest = (questionIds) => {

        const firstRemovedIndex = pagedTestQuestionData.findIndex(q => questionIds.includes(q.questionId));
        if (firstRemovedIndex !== -1) {
            pendingSelectedFocusIndexRef.current = firstRemovedIndex;
        }

        const updatedQuestions = testQuestionData.filter(question => !questionIds.includes(question.questionId));

        const reindexedQuestions = reIndexArray(updatedQuestions, ['questionSeq', 'index']);
        setTestQuestionData(reindexedQuestions);

        let newTotalPages = 1;

        if (useSections) {
            const updateSectionsObj = {...sectionsObj};

            const remainingSectionQuestions = updateSectionsObj[sectionTabVal].questions.filter(
                question => !questionIds.includes(question.questionId)
            );

            if (remainingSectionQuestions.length === 0) {
                delete updateSectionsObj[sectionTabVal];

                if (Object.keys(updateSectionsObj).length > 0) {
                    const firstKey = Object.keys(updateSectionsObj)[0];

                    // Map the newly re-indexed numbers to the fallback tab
                    const newTabQuestions = updateSectionsObj[firstKey].questions.map(q => reindexedQuestions.find(rq => rq.questionId === q.questionId) || q);
                    updateSectionsObj[firstKey].questions = newTabQuestions;

                    newTotalPages = Math.ceil(newTabQuestions.length / DEFAULT_PAGE_SIZE);
                    setSectionTabVal(firstKey);
                    setSelectedQuestionsSection(newTabQuestions);
                } else {
                    newTotalPages = 0;
                    setSectionTabVal(null);
                    setSelectedQuestionsSection([]);
                }

                let globalCounter = 0;
                const flattened = [];

                sectionTabs.forEach(tab => {
                    if (updateSectionsObj[tab.key]) {
                        updateSectionsObj[tab.key].questions = updateSectionsObj[tab.key].questions.map((q, localIdx) => {
                            const updatedQ = {
                                ...q,
                                sectionIndex: localIdx,
                                questionSeq: globalCounter++
                            };
                            flattened.push(updatedQ);
                            return updatedQ;
                        });
                    }
                });
            } else {
                const mappedRemaining = remainingSectionQuestions.map(q => reindexedQuestions.find(rq => rq.questionId === q.questionId) || q);
                updateSectionsObj[sectionTabVal].questions = mappedRemaining;
                setSelectedQuestionsSection(mappedRemaining);
                newTotalPages = Math.ceil(mappedRemaining.length / DEFAULT_PAGE_SIZE);
            }

            Object.keys(updateSectionsObj).forEach(key => {
                updateSectionsObj[key].questions = updateSectionsObj[key].questions.map(q => reindexedQuestions.find(rq => rq.questionId === q.questionId) || q);
            });

            setSectionsObj({...updateSectionsObj});
        } else {
            newTotalPages = Math.ceil(reindexedQuestions.length / DEFAULT_PAGE_SIZE);
        }

        if (currentPage > newTotalPages) {
            setCurrentPage(newTotalPages > 0 ? newTotalPages : 1);
        }

        changeMadeToTest();
        setSrAnnouncement(`${questionIds.length} question${questionIds.length !== 1 ? 's' : ''} removed from the test.`);
    };

    const handleEditTestQuestion = (obj) => {
        if(userConfig.user.adminUser){
            editTestQuestionInCMS(obj, userConfig.user.useReact);
        }
    }

    const handleMoveQuestion = (localIndex1, localIndex2) => {
        if (useSections) {
            // 1. Swap safely within the active tab's array
            const swappedSection = swapItemsInArray([...selectedQuestionsSection], localIndex1, localIndex2);

            const updateSectionsObj = {
                ...sectionsObj,
                [sectionTabVal]: {
                    ...sectionsObj[sectionTabVal],
                    questions: swappedSection
                }
            };

            // 2. Rebuild BOTH local and global indices based strictly on tab order
            let globalCounter = 0;
            const flattened = [];

            sectionTabs.forEach(tab => {
                if (updateSectionsObj[tab.key]) {
                    updateSectionsObj[tab.key].questions = updateSectionsObj[tab.key].questions.map((q, localIdx) => {
                        const updatedQ = {
                            ...q,
                            sectionIndex: localIdx,       // The explicit local position
                            questionSeq: globalCounter++  // The explicit global position
                        };
                        flattened.push(updatedQ);
                        return updatedQ;
                    });
                }
            });

            setSectionsObj(updateSectionsObj);
            setTestQuestionData(flattened);
            setSelectedQuestionsSection([...updateSectionsObj[sectionTabVal].questions]);
        } else {
            const swapped = swapItemsInArray([...testQuestionData], localIndex1, localIndex2);
            const reindexed = swapped.map((q, idx) => ({ ...q, questionSeq: idx, sectionIndex: idx }));
            setTestQuestionData(reindexed);
        }
        changeMadeToTest();
    }

    const handleAddAllQuestionsOnPage = () => {
        if(questionBankQuestions.length !== 0){
            addQuestionsToTest(questionBankQuestions);
        }
    }

    const removeSelectedQuestionsFromTest = () => {
        removeQuestionsFromTest(checkedQuestionIds);
        setCheckedQuestionIds([]);
    }

    useEffect(() => {
        if(testId !== null){
            getAndSetTestData(testId).then((data) => {
                setTestData(data);
            });
        }
    }, [testId]);

    useEffect(() => {
        if (objectHasEntry(testData)) {
            setTitle(testData.name);
            setDescription(testData.description);
            setUseSections(testData.exam && !testData.autoMark);
            getAndSetTestQuestionData(testId).then((data) => {
                setTestQuestionData(data);
            }).finally(() => {
                setInitTestLoading(false);
            });
        }
    }, [testData])

    useEffect(() => {
        if (useSections) {
            if (testDataInit) {
                if (selectedQuestionsSection.length === 0) {
                    setPagedTestQuestionData([]);
                } else {
                    const startIndex = (currentPage - 1) * DEFAULT_PAGE_SIZE,
                        endIndex = startIndex + DEFAULT_PAGE_SIZE;
                    setPagedTestQuestionData(selectedQuestionsSection.slice(startIndex, endIndex));
                }
            } else {
                if (testQuestionData.length > 0) {
                    const sectionObj = {};
                    testQuestionData.forEach((testQuestion, idx) => {
                        const sectionVal = testQuestion.section;
                        testQuestion.questionSeq = idx;
                        if (testQuestion.originalSeq === undefined) {
                            testQuestion.originalSeq = idx;
                        }
                        if (!sectionObj.hasOwnProperty(sectionVal)) {
                            testQuestion.sectionIndex = 0;
                            sectionObj[sectionVal] = {name: sectionVal, hash: sectionVal, questions: [testQuestion]}
                        } else {
                            testQuestion.sectionIndex = sectionObj[sectionVal].questions.length;
                            sectionObj[sectionVal].questions.push(testQuestion);
                        }
                    });
                    setSectionsObj(sectionObj);
                } else {
                    setSectionsObj({});
                }
                setTestDataInit(true);
            }
        } else {
            if (!testDataInit && testQuestionData.length > 0) {
                const mappedData = testQuestionData.map((q, idx) => ({
                    ...q,
                    sectionIndex: idx,
                    questionSeq: idx,
                    originalSeq: q.originalSeq !== undefined ? q.originalSeq : idx
                }));
                setTestQuestionData(mappedData);
                setTestDataInit(true);
            } else {
                if (testQuestionData.length === 0) {
                    setPagedTestQuestionData([]);
                } else {
                    const startIndex = (currentPage - 1) * DEFAULT_PAGE_SIZE,
                        endIndex = startIndex + DEFAULT_PAGE_SIZE;
                    setPagedTestQuestionData(testQuestionData.slice(startIndex, endIndex))
                }
            }
        }
    }, [currentPage, testQuestionData]);

    useEffect(()=>{
        setCheckedQuestionIds([]);
    },[currentPage,sectionTabVal])

    useEffect(() => {
        setSelectedQuestionIds(testQuestionData.map(question => question.questionId));
    }, [testQuestionData]);

    useEffect(() => {
        if (sectionsObj !== null) {
            if (sectionTabVal === null && objectHasEntry(sectionsObj)) {
                setSectionTabVal(Object.keys(sectionsObj)[0]);
            }
            setSectionTabs(Object.entries(sectionsObj).map(([key, value]) => {
                const totalCount = Array.isArray(value.questions) ? value.questions.length : 0;
                return {key: key, label: key, total: totalCount+''}
            }));
        }
    }, [sectionsObj]);

    useEffect(() => {
        if (sectionTabVal !== null && sectionsObj.hasOwnProperty(sectionTabVal)
            && sectionsObj[sectionTabVal].hasOwnProperty('questions')
        ) {
            setSelectedQuestionsSection(sectionsObj[sectionTabVal].questions);
        } else {
            setSelectedQuestionsSection([]);
        }
    }, [sectionTabVal])

    const handleQuestionBankExpand = (expand, id)=>{
        let updatedState;
        if(expand){
            updatedState = [...expandedQuestionBankIds, id]
        }else{
            updatedState = expandedQuestionBankIds.filter((questionId)=>{ return id !== questionId })
        }
        setExpandedQuestionBankIds(updatedState);
    }

    const handleSelectedQuestionExpand = (expand, id)=>{
        let updatedState;
        if(expand){
            updatedState = [...expandedSelectedQuestionIds,id]
        }else{
            updatedState = expandedSelectedQuestionIds.filter((questionId)=>{ return id !== questionId })
        }
        setExpandedSelectedQuestionIds(updatedState);
    }

    const handleExpandAllQuestionBank = (val)=>{
        if(val){
            setExpandedQuestionBankIds([...currentQuestionBankPageIds]);
        }else{
            setExpandedQuestionBankIds([]);
        }
    }
    const handleExpandAllSelectedQuestions = (val)=>{
        if(val){
            setExpandedSelectedQuestionIds(pagedTestQuestionData.map((question)=>{return question.questionId}));
        }else{
            setExpandedSelectedQuestionIds([]);
        }
    }

    useEffect(()=>{
        setQuestionBankExpandAll((expandedQuestionBankIds.length > 0 && currentQuestionBankPageIds.length === expandedQuestionBankIds.length));
    },[expandedQuestionBankIds])

    useEffect(()=>{
        setSelectedQuestionsExpandAll((expandedSelectedQuestionIds.length > 0 && pagedTestQuestionData.length === expandedSelectedQuestionIds.length));
    },[expandedSelectedQuestionIds])

    const handleQuestionBankReady = (data) => {
        setCurrentQuestionBankPageData(data);
        setCurrentQuestionBankPageIds(data.map((question) => question.questionId));
        setSrAnnouncement(`Search complete. Showing ${data.length} questions.`);
    }

    useEffect(() => {
        setQuestionBankQuestions(
            currentQuestionBankPageData.filter(question => !selectedQuestionIds.includes(question.questionId))
        );
    }, [currentQuestionBankPageData, selectedQuestionIds]);

    useEffect(() => {
        if (pendingBankFocusIndexRef.current !== null) {
            const bankPanel = document.querySelector('#question-bank-list');

            if (bankPanel) {
                const remainingItems = Array.from(bankPanel.querySelectorAll('.ch-list-item'));

                if (remainingItems.length > 0) {
                    let targetIndex = pendingBankFocusIndexRef.current;

                    if (targetIndex + 1 < remainingItems.length) {
                        targetIndex++;
                    }

                    const targetCard = remainingItems[targetIndex];

                    const focusableButtons = Array.from(targetCard.querySelectorAll('button:not([disabled]), input:not([disabled]):not([type="hidden"])'));

                    if (focusableButtons.length > 0) {
                        const targetButton = focusableButtons.find(btn =>
                            btn.getAttribute('aria-label')?.toLowerCase().includes('add')
                        ) || focusableButtons[0];

                        setTimeout(() => targetButton.focus(), 0);
                    } else {
                        questionBankHeadingRef.current?.focus();
                    }
                } else {
                    questionBankHeadingRef.current?.focus();
                }
            }

            pendingBankFocusIndexRef.current = null;
        }
    }, [selectedQuestionIds]);

    const handleSplitViewClick = (e)=>{
        setSelectedTags([View.QUESTION_BANK, View.SELECTED_QUESTIONS]);
        setSrAnnouncement("Switched to split view.");
    }

    const RenderQuestionBankHeading = useMemo(()=>{
        return (<header>
            <div className={'ch-test-editor-content-panel-heading'}>
                {!mobileScreenMode && <h3>Question Bank</h3>}
                <ButtonsGroup>
                    <Tooltip title={'Add questions on page ('+questionBankQuestions.length+')'}>
                        <Button className="js-add-question-btn" aria-label={'Add questions on page ('+questionBankQuestions.length+')'} icon={<Add/>} onClick={handleAddAllQuestionsOnPage} type={'text'}></Button>
                    </Tooltip>
                    <Tooltip title={isQuestionBankExpandAll?`Collapse question content`: `Expand question content`}>
                        <Button icon={<ChevronRightRounded
                            style={{transform: isQuestionBankExpandAll ? 'rotate(270deg)' : 'rotate(90deg)', transition: 'var(--ani-mid) ease'}}/>}
                                aria-label={isQuestionBankExpandAll?`Collapse question content`: `Expand question content`} onClick={()=>{handleExpandAllQuestionBank(!isQuestionBankExpandAll)}} type={'text'}
                                aria-expanded={isQuestionBankExpandAll}/>
                    </Tooltip>
                    {(selectedTags.length === 2 && !mobileScreenMode) && <Tooltip title={`Full width view 'Question Bank'`}>
                        <Button aria-label="Switch to full width view for Question Bank" icon={<FitScreenRounded/>} onClick={()=>{setSelectedTags([View.QUESTION_BANK]);setSrAnnouncement("Switched to full width view for Question Bank.");}} type={'text'}/>
                    </Tooltip>}
                    {(selectedTags.length === 1 && !mobileScreenMode) && <Tooltip title={'Split view'}>
                        <Button aria-label={'Switch to split view'} icon={<SplitscreenTwoTone style={{background:'white', transform:'rotate(90deg)'}}/>} onClick={(e)=>{handleSplitViewClick(e)}} type={'text'}/>
                    </Tooltip>}
                </ButtonsGroup>
            </div>
            <Divider className={'ch-divider'}/>
        </header>)
    },[selectedTags, mobileScreenMode, isQuestionBankExpandAll, questionBankQuestions]);

    const handle2DGridNavigation = (e, rovingHookProps) => {
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
            if (rovingHookProps.onKeyDown) rovingHookProps.onKeyDown(e);
            return;
        }

        const currentElement = document.activeElement;
        const currentRow = currentElement.closest('.ch-list-item');

        if (!currentRow) {
            if (rovingHookProps.onKeyDown) rovingHookProps.onKeyDown(e);
            return;
        }

        e.preventDefault();

        const targetRow = e.key === 'ArrowDown'
            ? currentRow.nextElementSibling
            : currentRow.previousElementSibling;

        if (targetRow) {
            const currentRect = currentElement.getBoundingClientRect();
            const currentCenter = currentRect.left + (currentRect.width / 2);

            const targetRowElements = Array.from(
                targetRow.querySelectorAll('button:not([disabled]), input:not([disabled]):not([type="hidden"])')
            );

            if (targetRowElements.length === 0) return;

            let closestElement = null;
            let minDistance = Infinity;

            const currentAria = currentElement.getAttribute('aria-label');
            const normalizeAria = (aria) => aria ? aria.replace(/\d+/g, '').trim() : null;
            const normalizedCurrentAria = normalizeAria(currentAria);

            targetRowElements.forEach(el => {
                const rect = el.getBoundingClientRect();
                const center = rect.left + (rect.width / 2);
                let distance = Math.abs(currentCenter - center);

                const targetAria = el.getAttribute('aria-label');
                const isSameAria = normalizedCurrentAria && targetAria && normalizedCurrentAria === normalizeAria(targetAria);

                const hasContent = currentElement.innerHTML.trim() !== '';
                const isSameIcon = hasContent && currentElement.innerHTML === el.innerHTML;

                if (isSameAria || isSameIcon) {
                    distance -= 1000;
                }

                if (distance < minDistance) {
                    minDistance = distance;
                    closestElement = el;
                }
            });

            if (closestElement) {
                closestElement.focus();
            }
        }
    };

    const questionBankPanelRoving = useIndexRoving('button, input')

    const renderQuestionBankPanel = useMemo(() => {
        const TestQuestionCardInBank = (question, index, value) => {
            const propsForQuestionBankQuestion = {
                ...question, initialIndex: index,
                test: {exam: testData.exam, diagnosticTest: testData.diagnosticTest},
                key: question.questionId, editTestQuestion: handleEditTestQuestion, inQuestionBank: true,
                questionLevelNames,
                onAdd: () => {
                    pendingBankFocusIndexRef.current = index;
                    addQuestionsToTest([question]);
                }
            };
            if (selectedQuestionIds.includes(question.questionId)) {
                propsForQuestionBankQuestion.selectedForTest = true;
            }
            return <TestQuestionCard handleExpandChange={handleQuestionBankExpand} expanded={expandedQuestionBankIds.includes(question.questionId)} {...propsForQuestionBankQuestion} />
        }
        return (
            <section aria-label="Question Bank List" className={(mobileScreenMode ? 'ch-test-editor-content-panel-ac' : 'ch-test-editor-content-panel') + ' question-content-holder'}>
                {!mobileScreenMode && RenderQuestionBankHeading}
                <ListAntD id={'question-bank-list'}
                          roving={{
                              ...questionBankPanelRoving,
                              onKeyDown: (e) => handle2DGridNavigation(e, questionBankPanelRoving)
                          }}
                          showBackgroundOnHover={false} removeListPadding={true}
                          pagingDependencies={[searchTestQuestionsParams]} pagingData={{
                    fetchFunction: searchTestQuestionsPaged,
                    params: {...searchTestQuestionsParams, pageSize: DEFAULT_PAGE_SIZE},
                    onReady: handleQuestionBankReady,
                }}
                          noDataMessage={<EmptyDataCard message={'No results found, try changing your search.'}/>}
                          position={'bottom'}
                          renderItem={(question, index, value) => (
                              TestQuestionCardInBank(question, index, value)
                          )}/>
            </section>
        )
    }, [searchTestQuestionsParams, selectedQuestionIds, selectedTags,
        expandedQuestionBankIds, isQuestionBankExpandAll, currentQuestionBankPageIds,
        questionBankQuestions, showSelectedQuestions, mobileScreenMode,
        questionBankPanelRoving.ref,
        questionBankPanelRoving.onKeyDown,
        questionBankPanelRoving.onFocus,
        questionBankPanelRoving.onClick,
        questionLevelNames]);

    const handlePageChange = useCallback((pageNumber) => {
        setCurrentPage(pageNumber);
    }, []);
    const selectedQuestionPaging = usePaginationRoving();
    const renderSelectedQuestionsPagination = useMemo(() => {
        const totalItems = useSections?selectedQuestionsSection.length:testQuestionData.length;
        const customItemRender = getAccessibleItemRender({
            currentPage: currentPage,
            totalResults: totalItems,
            pageSize: DEFAULT_PAGE_SIZE,
            showOnePageTotal: totalItems <= DEFAULT_PAGE_SIZE
        });
        if (useSections) {
            return <Flex className={'ch-pagination-wrapper'} ref={selectedQuestionPaging.ref} onClick={selectedQuestionPaging.onClick} onFocus={selectedQuestionPaging.onFocus} onKeyDown={selectedQuestionPaging.onKeyDown}>{selectedQuestionsSection.length > 0 &&
                <Pagination style={{margin: 0}} onChange={handlePageChange} current={currentPage}
                            pageSize={DEFAULT_PAGE_SIZE} defaultCurrent={1}
                            showSizeChanger={false}
                            total={selectedQuestionsSection.length}
                            itemRender={customItemRender}
                            size={'small'}
                />}</Flex>
        } else {
            return <Flex className={'ch-pagination-wrapper'} ref={selectedQuestionPaging.ref} onClick={selectedQuestionPaging.onClick} onFocus={selectedQuestionPaging.onFocus} onKeyDown={selectedQuestionPaging.onKeyDown}>{testQuestionData.length > 0 &&
                <Pagination style={{margin: 0}} onChange={handlePageChange} current={currentPage}
                            showSizeChanger={false}
                            pageSize={DEFAULT_PAGE_SIZE} defaultCurrent={1} total={testQuestionData.length}
                            itemRender={customItemRender}
                            size={'small'}
                />}</Flex>
        }
    }, [currentPage, testQuestionData.length, selectedQuestionsSection.length, useSections, selectedQuestionPaging]);

    const onSectionTabChange = (val) => {
        if (testDataInit && selectedQuestionsSection.length > 0) {
            const updateSectionsObj = {...sectionsObj};
            updateSectionsObj[sectionTabVal].questions = [...selectedQuestionsSection];
            setSectionsObj({...updateSectionsObj});
        }
        setSectionTabVal(val);
        if (sectionsObj.hasOwnProperty(val)) {
            const questions = checkArrayVal(sectionsObj[val].questions);
            setSelectedQuestionsSection(questions);
            setCurrentPage(1);
        }
    }


    useEffect(() => {
        if (selectedQuestionsSection.length === 0) {
            setPagedTestQuestionData([]);
        } else {
            const startIndex = (currentPage - 1) * DEFAULT_PAGE_SIZE,
                endIndex = startIndex + DEFAULT_PAGE_SIZE;
            setPagedTestQuestionData(selectedQuestionsSection.slice(startIndex, endIndex));
        }
    }, [selectedQuestionsSection]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setCheckedQuestionIds(pagedTestQuestionData.map(question => question.questionId));
        } else {
            setCheckedQuestionIds([]);
        }
    }

    const isSelectAllQuestions = pagedTestQuestionData.length > 0 && pagedTestQuestionData.every(q => checkedQuestionIds.includes(q.questionId));

    const onCloseReorderView = (confirmReorderVal)=>{
        setShowEditOrderView(false);
        setSelectedTags(tagsBeforeReordering);
        if(confirmReorderVal){
            handleReorder();
        }
        setTimeout(()=>{
            reorderBtnRef.current?.focus();
        },0);
    }

    const handleCancelReorder = ()=>{
        onCloseReorderView(false);
    }

    const handleConfirmReorder = ()=>{
        onCloseReorderView(true);
    }

    const RenderSelectedQuestionsHeading = useMemo(()=>{
        return (<header>
            <div className={'ch-test-editor-content-panel-heading'}>
                <h3 ref={selectedQuestionsHeadingRef} tabIndex={-1} style={{ outline: 'none' }} className={'flexAlignCentre smallGap'}>
                    {!showEditOrderView && (
                        <Checkbox
                            id="select-all-selected-questions"
                            aria-label={isSelectAllQuestions ? 'Deselect all questions' : 'Select all questions'}
                            disabled={showEditOrderView}
                            checked={isSelectAllQuestions}
                            onChange={handleSelectAll}
                        />
                    )}
                    <label htmlFor="select-all-selected-questions" style={{ cursor: 'pointer' }}>
                        {!mobileScreenMode ? <Space>Selected Questions<Badge className={'badge-always-active'} count={selectedQuestionIds.length}/></Space> : <span className={'ch-std-font'}>Select all</span>}
                    </label>
                </h3>
                <ButtonsGroup>
                    {!showEditOrderView && <Tooltip title={`Remove selected questions (${checkedQuestionIds.length})`}>
                        <Button aria-label={'Remove selected questions'} onClick={removeSelectedQuestionsFromTest} type={'text'}><DeleteRounded aria-hidden={'true'}/></Button>
                    </Tooltip>}
                    {!showEditOrderView && <Tooltip title={isSelectedQuestionsExpandAll ? 'Collapse question content' : 'Expand question content'}>
                        <Button aria-label={isSelectedQuestionsExpandAll ? 'Expand question content' : 'Collapse question content'} onClick={()=>{handleExpandAllSelectedQuestions(!isSelectedQuestionsExpandAll)}} type={'text'}>
                            <ChevronRightRounded aria-hidden={'true'}
                                                 style={{transform: isSelectedQuestionsExpandAll ? 'rotate(270deg)':  'rotate(90deg)', transition: 'var(--ani-mid) ease'}}/>
                        </Button>
                    </Tooltip>}
                    {!showEditOrderView && <Tooltip title={'Reorder questions'}>
                        <Button ref={reorderBtnRef}  aria-label={'Reorder questions'} onClick={()=>{setShowEditOrderView(true); setTagsBeforeReordering(selectedTags); setSelectedTags([View.SELECTED_QUESTIONS])}} type={'text'}><Reorder aria-hidden={'true'}/></Button>
                    </Tooltip>}
                    {showEditOrderView && <Button ref={reorderTopRef} aria-label={'Cancel Order: Stop reordering questions'} onClick={handleCancelReorder} icon={<CloseRounded/>} iconPosition={BUTTON_ICON_POSITION.RIGHT} type={BUTTON_TYPE.TEXT}>Cancel Order</Button>}
                    {showEditOrderView && <Button aria-label={'Confirm question reordering'} onClick={handleConfirmReorder} icon={<CheckCircleOutline/>} iconPosition={BUTTON_ICON_POSITION.RIGHT}>Confirm Order</Button>}
                    {(selectedTags.length === 2 && !mobileScreenMode) && <Tooltip onClick={()=>{setSelectedTags([View.SELECTED_QUESTIONS])}} title={`Full width view 'Selected Questions'`}>
                        <Button aria-label={`Full width view 'Selected Questions'`} type={'text'}><FitScreenRounded aria-hidden={'true'}/></Button>
                    </Tooltip>}
                    {(selectedTags.length === 1 && !showEditOrderView && !mobileScreenMode) && <Tooltip title={'Split view'}>
                        <Button aria-label={`Split view for Question Bank and Selected Questions`} onClick={(e)=>{handleSplitViewClick(e)}} type={'text'}><SplitscreenTwoTone aria-hidden={'true'} style={{background:'white', transform:'rotate(90deg)'}}/></Button>
                    </Tooltip>}
                </ButtonsGroup>
            </div>
            <Divider className={'ch-divider'}/>
        </header>)
    },[selectedTags, showEditOrderView, tagsBeforeReordering,
        isSelectAllQuestions, selectedQuestionIds, isSelectedQuestionsExpandAll,
        checkedQuestionIds, mobileScreenMode, pagedTestQuestionData])

    useEffect(()=>{
        if(mobileScreenMode){
            setShowEditOrderView(false);
        }
    },[mobileScreenMode])
    const questionListRoving = useIndexRoving('input, button');

    const renderSelectedQuestionsPanel = useMemo(() => {
        return (<section aria-label="Selected Questions List" className={mobileScreenMode ? 'ch-test-editor-content-panel-ac' : 'ch-test-editor-content-panel'}>
            {!mobileScreenMode && RenderSelectedQuestionsHeading}
            {!initTestLoading && (showEditOrderView ? (objectHasEntry(reorderData) && <ReorderView ref={reorderViewRef} useSections={useSections} maxPageSize={DEFAULT_PAGE_SIZE} test={{
                exam: testData.exam,
                diagnosticTest: testData.diagnosticTest
            }} initialData={reorderData} />) : (<>
                {(useSections && sectionTabs.length > 0) &&
                    <Tabs onChange={onSectionTabChange} activeKey={sectionTabVal}>
                        {sectionTabs.map((tab) => {
                            return <Tab className={'smallGap'} key={tab.key} tab={<>
                                Section {tab.label} <Space><Badge className={'ch-sml-font tab-badge'} size={'medium'} count={tab.total} /></Space>
                            </>} hash={tab.key}/>
                        })}
                    </Tabs>
                }
                <ListAntD
                    id={'selected-questions-list'}
                    showBackgroundOnHover={false}
                    roving={{
                        ...questionListRoving,
                        onKeyDown: (e) => handle2DGridNavigation(e, questionListRoving)
                    }}
                    removeListPadding={true}
                    data={pagedTestQuestionData}
                    noDataMessage={<EmptyDataCard message={"Add questions to " + brandConfig.testName + " from the 'Question Bank'"}/>}
                    renderItem={(testQuestion)=>{
                        return <TestQuestionCard
                            initialIndex={testQuestion.questionSeq}  // Global display number (10, 11, 12...)
                            index={testQuestion.sectionIndex}        // Local section position for arrows (0, 1, 2...)
                            key={testQuestion.questionId}
                            questionTotal={useSections ? selectedQuestionsSection.length : testQuestionData.length}
                            {...testQuestion}
                            onRemove={() => {
                                removeQuestionsFromTest([testQuestion.questionId])
                            }} onMoveQuestion={handleMoveQuestion}
                            editTestQuestion={handleEditTestQuestion}
                            test={{
                                exam: testData.exam,
                                diagnosticTest: testData.diagnosticTest
                            }}
                            handleCheckboxChange={handleCheckboxChange}
                            handleExpandChange={handleSelectedQuestionExpand}
                            checkboxChecked={checkedQuestionIds.includes(testQuestion.questionId)}
                            expanded={expandedSelectedQuestionIds.includes(testQuestion.questionId)}
                            selectedForTest={true} inQuestionBank={false}/>
                    }}
                />
                <div className={'flexJustifyEnd'}>{renderSelectedQuestionsPagination}</div>
            </>))}
            {initTestLoading && <div style={{marginTop: '25px'}} className={'flexCentre'}><Spin size={"large"}/></div>}
        </section>)
    }, [initTestLoading, pagedTestQuestionData, testQuestionData,
        sectionTabs, selectedTags, checkedQuestionIds, isSelectAllQuestions,
        expandedSelectedQuestionIds, isSelectedQuestionsExpandAll, reorderData,
        showEditOrderView, showQuestionBank, mobileScreenMode,
        questionListRoving.ref,
        questionListRoving.onKeyDown,
        questionListRoving.onFocus,
        questionListRoving.onClick,
        sectionsObj, sectionTabVal, selectedQuestionsSection])


    const ContentSelect = useMemo(() => {
        if(objectHasEntry(testData)){
            return <TestContentSelect onChange={searchTestQuestions} axiosProps={{forExams: testData.exam}}
                                      defaultLevel={SELECT_LEVELS.SYLLABUS} form={questionFiltersSearchForm}
                                      popupMatchSelectWidth={false}/>
        }
        return <></>;
    }, [testData]);

    const onTabChange = (value) => {
        if (value) {
            setContentTabVal(value);
            questionFiltersSearchForm.resetFields();
        }
    }

    const searchAndAdd = (val)=>{
        setSkillSummaryDialogOpen(false);
        if(val.curriculum){
            onTabChange(ContentTabs.Curriculum.val);
            setSearchSkillData({
                syllabusId:val.curriculumSyllabusId,
                topicGroupId:val.curriculumTopicGroupId,
                topicId:val.curriculumTopicId,
                conceptId:val.curriculumConceptId,
                skillIds:[val.skillId],
            })
        }else{
            onTabChange(ContentTabs.Text.val);
            setSearchSkillData({
                syllabusId:val.textbookSyllabusId,
                topicGroupId:val.curriculumTopicGroupId,
                topicId:val.textbookTopicId,
                conceptId:val.textbookConceptId,
                skillIds:[val.skillId],
            })
        }
    }

    const handleSetSkillData = (obj)=>{
        setSearchSkillData(obj);
    }

    const handleQuestionLevelNameChange = ((questionLevelNames)=>{
        setQuestionLevelNames(questionLevelNames);
    });

    return (
        <>
            <div id={'test-editor'}>
                <LiveRegion message={srAnnouncement} />
                {showBreadCrumbs && <BreadcrumbBar/>}
                {initTestLoading && <div className={'flexCentre padding-t--l padding-b--l'}
                                         role="status"
                >
                    <Spin description="Loading Editor..." size="large">
                        <div style={{
                            width: '100%',
                            height: '100%',
                            padding: '100px 200px',
                            borderRadius: '10px',
                            border: '1px solid #ccc'
                        }}></div>
                    </Spin></div>}
                {!initTestLoading && <article className={'ch-test-editor'}>
                    <header className={'ch-test-editor-sticky-title'}>
                        <div className={'ch-test-editor-heading-wrapper flexSpaceBetween mediumGap'}>
                            <div className={'ch-test-editor-heading'}>
                                <InputAntdInline placeholder={'name'} isLoading={saveTestNameLoading} onSave={(val) => {
                                    handleSaveName(val);
                                    setTitle(val);
                                }} value={title}/>
                                <InputAntdInline placeholder={'description'} isLoading={saveTestDescriptionLoading}
                                                 onSave={(val) => {
                                                     handleSaveDescription(val);
                                                     setDescription(val);
                                                 }} value={description} textType={'p'}
                                                 className={'ch-test-editor-description'}/>
                            </div>
                            <ButtonsGroup>
                                <Button
                                    type={'outline'}
                                    icon={<PreviewRounded/>}
                                    onClick={handlePreviewTest}
                                >
                                    Preview
                                </Button>
                                {testData.diagnosticTest && <Button
                                    type={'outline'}
                                    icon={<SummarizeRounded/>}
                                    onClick={handleSkillSummary}
                                >
                                    Skill Summary
                                </Button>}
                                <Button
                                    type={'outline'}
                                    icon={<PrintRounded/>}
                                    onClick={handlePrintTest}
                                >
                                    Print
                                </Button>
                                {useSections && <Button
                                    type={'outline'}
                                    icon={<FileDownloadRounded/>}
                                    onClick={handleDownloadDoc}
                                >
                                    Word Doc
                                </Button>}
                                <Button
                                    icon={<HelpRounded/>}
                                    type={'outline'}
                                    onClick={()=>{setHelpDialogOpen(true);}}
                                >
                                    Help
                                </Button>
                                <Button
                                    icon={<SaveRounded/>}
                                    onClick={handleSaveTest}
                                    loading={saveTestLoading}
                                    disabled={changesSaved}
                                >
                                    Save
                                </Button>
                            </ButtonsGroup>
                        </div>
                        <Divider className={'ch-divider'}/>
                    </header>
                    {objectHasEntry(testData) &&
                        <TableFilterControls
                            primaryFilterItem={testData.diagnosticTest && (<>
                                    <Form.Item label={'Search questions by:'}>
                                        <Select
                                            value={contentTabVal}
                                            onChange={onTabChange}
                                            options={[
                                                {label: ContentTabs.Text.display, value: ContentTabs.Text.val},
                                                {label: ContentTabs.Curriculum.display, value: ContentTabs.Curriculum.val}
                                            ]}
                                            style={{minWidth: 200}}
                                        />
                                    </Form.Item>
                                </>
                            )}
                            filterContent={
                                <TableFilterControlsForm
                                    initialValues={{
                                        excludeTestOnly: false,
                                        topicId: 0,
                                        conceptId: 0,
                                        skillCode: 0,
                                        ...(testData.exam ? {
                                            examStyle: 'All',
                                            examYear: 'All',
                                            examNumber: 'All',
                                            section: 'All',
                                            testDifficultyLevel: 'All',
                                            useCalculator: 'All',
                                            questionAnswerTypeGroup: testData.autoMark ? 'MULTIPLE_CHOICE' : 'All'
                                        } : testData.diagnosticTest ? {
                                            testDifficultyLevel: 'All',
                                            skillKeyword: ''
                                        } : {
                                            testDifficultyLevel: 'All',
                                            digitalOnly: true
                                        })
                                    }}
                                    form={questionFiltersSearchForm}
                                    additionalFilterSections={(() => {
                                        if (testData.exam) {
                                            return [<ExamFilters key="exam-filters" onLoadComplete={onLoadComplete}
                                                                 onUpdate={searchTestQuestions}
                                                                 isAutoMark={testData.autoMark}
                                                                 syllabusId={syllabusId}
                                                                 handleQuestionLevelNameChange={handleQuestionLevelNameChange}/>];
                                        }
                                        if (testData.diagnosticTest) {
                                            return [<DiagnosticTestFilters key="diagnostic-filters"
                                                                           onLoadComplete={onLoadComplete}
                                                                           onUpdate={searchTestQuestions}/>];
                                        }
                                        return [<TestFilters key="test-filters" onLoadComplete={onLoadComplete}
                                                             onUpdate={searchTestQuestions}/>];
                                    })()}
                                >
                                    {!testData.diagnosticTest &&
                                        <TableFilterControlsSection showDivider={false}>
                                            {ContentSelect}
                                        </TableFilterControlsSection>
                                    }
                                    {testData.diagnosticTest &&
                                        <TableFilterControlsSection showDivider={false}>
                                            <DiagnosticTestContentSelect
                                                key={contentTabVal === ContentTabs.Text.val ? ContentTabs.Text.selectKey : ContentTabs.Curriculum.selectKey}
                                                onChange={searchTestQuestions}
                                                form={questionFiltersSearchForm}
                                                searchSkillData={searchSkillData}
                                                handleSetSkillData={handleSetSkillData}
                                                diagnosticTabType={contentTabVal === ContentTabs.Text.val ? DiagnosticTabType.TEXT : DiagnosticTabType.CURRICULUM}
                                            />
                                        </TableFilterControlsSection>
                                    }
                                </TableFilterControlsForm>
                            }/>}
                    <div className={'ch-test-editor-content-panels-wrapper margin-t--l'}
                         ref={contentPanelsRef}>
                        {!mobileScreenMode &&
                            <div id={'ch-test-editor-content-panels'}>
                                {(showQuestionBank && !showEditOrderView) && renderQuestionBankPanel}
                                {showSelectedQuestions && renderSelectedQuestionsPanel}
                            </div>
                        }
                        {mobileScreenMode &&
                            <Collapse
                                defaultActiveKey={['1']}
                                items={[
                                    {
                                        key: '1',
                                        showArrow: false,
                                        label: <span className={'ch-lrg-font'}>Question Bank</span>,
                                        children: <>{RenderQuestionBankHeading}{renderQuestionBankPanel}</>
                                    },
                                    {
                                        key: '2',
                                        showArrow: false,
                                        label: <span className={'ch-lrg-font'}>Selected Questions ({selectedQuestionIds.length})</span>,
                                        children: <>{RenderSelectedQuestionsHeading}{renderSelectedQuestionsPanel}</>
                                    },
                                ]}
                                ghost
                                style={{width: '100%'}}
                            />
                        }
                    </div>
                </article>}
            </div>
            <PreviewTestDialog title={brandConfig.testName+' ' + title + ' Preview'}
                               open={isPreviewDialogOpen}
                               onClose={() => setPreviewDialogOpen(false)}
                               testId={testId}
            />
            <TestHelpDialog
                open={isHelpDialogOpen}
                onClose={() => setHelpDialogOpen(false)}
                isDiagnostic={testData.diagnosticTest}
                diagnosticName={brandConfig.diagnosticName}
                buttons={[
                    { name: 'Close' },
                ]}
            />
            {testData.diagnosticTest && <TestSkillSummaryDialog title={'Test Skill Summary'}
                                                                open={isSkillSummaryDialogOpen}
                                                                onClose={() => setSkillSummaryDialogOpen(false)}
                                                                testId={testId}
                                                                searchAndAdd={searchAndAdd}
            />}
        </>
    );
}
export default TestEdit;